const TURNSTILE_SCRIPT_ID = 'turnstile-script';
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise = null;
let activeChallengePromise = null;

const getSiteKey = () => (
    process.env.REACT_APP_TURNSTILE_KEY ||
    ''
);

const loadTurnstileScript = () => {
    if (window.turnstile) {
        return Promise.resolve();
    }

    if (scriptPromise) {
        return scriptPromise;
    }

    scriptPromise = new Promise((resolve, reject) => {
        const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
        if (existingScript) {
            existingScript.addEventListener('load', () => resolve(), { once: true });
            existingScript.addEventListener('error', () => reject(new Error('Failed to load Turnstile script')), { once: true });
            return;
        }

        const script = document.createElement('script');
        script.id = TURNSTILE_SCRIPT_ID;
        script.src = TURNSTILE_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Turnstile script'));
        document.head.appendChild(script);
    });

    return scriptPromise;
};

const ensureTurnstileReady = async () => {
    const siteKey = getSiteKey();
    if (!siteKey) {
        throw new Error('Turnstile site key is missing');
    }

    await loadTurnstileScript();

    if (!window.turnstile) {
        throw new Error('Turnstile is not available');
    }

    return siteKey;
};

const createWidgetContainer = () => {
    const container = document.createElement('div');
    container.setAttribute('aria-hidden', 'true');
    container.style.position = 'fixed';
    container.style.bottom = '0';
    container.style.left = '0';
    container.style.width = '1px';
    container.style.height = '1px';
    container.style.opacity = '0';
    container.style.pointerEvents = 'none';
    document.body.appendChild(container);

    return container;
};

const cleanupWidget = (container, id) => {
    if (window.turnstile && id !== null && typeof window.turnstile.remove === 'function') {
        try {
            window.turnstile.remove(id);
        } catch (_error) {
            // Fall back to removing the container directly when remove() is unavailable.
        }
    }

    if (container && container.parentNode) {
        container.parentNode.removeChild(container);
    }
};

const runTurnstileChallenge = (siteKey, action) => new Promise((resolve, reject) => {
    let settled = false;
    let currentWidgetId = null;
    const widgetContainer = createWidgetContainer();

    const finalize = (handler, value) => {
        if (settled) {
            return;
        }

        settled = true;
        clearTimeout(timeoutId);
        cleanupWidget(widgetContainer, currentWidgetId);
        handler(value);
    };

    const timeoutId = window.setTimeout(() => {
        finalize(reject, new Error('Turnstile challenge timed out'));
    }, 20000);

    try {
        currentWidgetId = window.turnstile.render(widgetContainer, {
            sitekey: siteKey,
            size: 'invisible',
            execution: 'render',
            action,
            callback: (token) => {
                finalize(resolve, token);
            },
            'error-callback': () => {
                finalize(reject, new Error('Turnstile verification failed'));
            },
            'expired-callback': () => {
                finalize(reject, new Error('Turnstile challenge expired'));
            }
        });
    } catch (error) {
        finalize(
            reject,
            error instanceof Error ? error : new Error('Failed to start Turnstile challenge')
        );
    }
});

const executeTurnstile = async (action) => {
    if (activeChallengePromise) {
        return activeChallengePromise;
    }

    // Reuse one in-flight challenge so rapid clicks do not spawn overlapping executes.
    activeChallengePromise = (async () => {
        const siteKey = await ensureTurnstileReady();
        return runTurnstileChallenge(siteKey, action);
    })().finally(() => {
        activeChallengePromise = null;
    });

    return activeChallengePromise;
};

export function useTurnstileAction() {
    const getTurnstileToken = async (action) => executeTurnstile(action);

    return { getTurnstileToken };
}
